import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import '@polymer/paper-listbox/paper-listbox.js'
import '@polymer/paper-item/paper-item.js'
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import template from './ns-slotted-dropdown.html';
import {FlattenedNodesObserver} from '@polymer/polymer/lib/utils/flattened-nodes-observer.js';

/**
 * <p>An Improvement on the standard ns-dropdown.
 * This dropdown is slotted to allow for more detail/versatility in selections,
 * and uses pub/sub for events.</p>
 *
 * <h2>UX/Design Docs</h2>
 * <a href="https://nuskin.invisionapp.com/d/main/#/console/13401975/279849559/inspect" target="_blank">
 *   <img class="ux-doc" src="https://nuskin.invisionapp.com/static-signed/live-embed/242207610/279849559/2/latest/xV9norSGOlEAdlEvdmzYh1kJCi7HS6d1uwMmccDlEmRXXpeuBfTCZAlEcuhvRAP51u6ZEtNt3odfkSPPGlEwmSWxsVAlE/Standard-Dropdown-2x.png" alt="Buttons UX Documentation">
 * </a>
 *
 * <h2>Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-slotted-dropdown>
 *      <paper-item data-value="item1">Item 1</paper-item>
 *      <paper-item data-value="item2">Item 2</paper-item>
 *      <paper-item data-value="item2">Item 3</paper-item>
 *     </ns-slotted-dropdown>
 *     <br>
 *     <p>Should have a demo showing an example using the dropdown-content slot</p>
 *   </template>
 * </demo-snippet>
 *
 * <marked-element>
 *  <div slot="markdown-html">
 *  <script type="text/markdown">
 *
 *      ### Styling
 *
 *      ##### `<ns-expressive-link>` provides the following custom properties and mixins for styling:
 *
 *      Custom property                 | Description                                           | Default
 *      --------------------------------|-------------------------------------------------------|-------------------
 *      `--tbd`                          | We should probably have a mixin for stuff            | `{}`
 *
 *      ### Slots
 *
 *      ##### `<ns-expressive-link>` provides the following slots for content:
 *
 *      Custom property                 | Description                           | Default
 *      --------------------------------|---------------------------------------|-------------------
 *      `dropdown-content`              | Override the entire content box - including the paper-listbox. Overrides the defualt slot                      | `<paper-listbox id="dropdownListbox"></paper-listbox`
 *      `default`                       | Content of the paper-listbox                       | ``
 *
 *  </script>
 * </marked-element>
 *
 */
export class NsSlottedDropdown extends PolymerElement {
    constructor() {
        super();
    }

    static get is() {
        return "ns-slotted-dropdown";
    }

    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    static get properties() {
        return {
            /**
             * If you want to use an attribute value or property of an element for
             * selected instead of the index, set this to the name of the attribute
             * or property. Hyphenated values are converted to camel case when used
             * to look up the property of a selectable element. Camel cased values
             * are not converted to hyphenated values for attribute lookup. It's
             * recommended that you provide the hyphenated form of the name so that
             * selection works in both cases. (Use attr-or-property-name instead of attrOrPropertyName.)
             * This gets passed on to the internal paper-listbox
             */
            attrForSelected: {
                type: String
            },
            /**
             * Optional parameter. If set to true, runs validation after closing the dropdown (default: false)
             */
            autovalidate: {
                type: Boolean,
                value: false
            },
            /**
             * Optional parameter to specify whether events that are fired by the component should bubble on not. Default - false
             */
            bubbles: {
                type: Boolean,
                value: false
            },
            /**
             * Optional parameter to specify whether events that are fired by the component are composed on not. Default - false
             */
            composed: {
                type: Boolean,
                value: false
            },
            /**
             * If true sets the dropdown to disabled
             */
            disabled: {
                type: Boolean,
                reflectToAttribute: true
            },
            /**
             * Optional parameter to specify index of the dropdown if used in the list of dropdowns.
             */
            index: {
                type: Number,
                notify: true
            },
            /**
             * Index of the current selection
             */
            initSelect: {
                type: Number,
                observer: '_processInitSelect'
            },
            /**
             * Notifies if the dropdown selection is invalid
             */
            invalid: {
                type: Boolean,
                observer: "showErrors",
                notify: true,
                value: false
            },
            keepSelectedAsHtml: {
                type: Boolean
            },
            /**
             * Label copy for the dropdown
             */
            label: {
                type: String,
                reflectToAttribute: true
            },
            /**
             * Represents the state of the content box. Does not open the dropdown, toggle() does.
             * This simply indicates the state
             */
            opened: {
                type: Boolean,
                value: false,
                reflectToAttribute: true,
                notify: true
            },
            /**
             * True as long as the dropdown has never been opened. As soon as the user
             * opens the dropdown once this becomes false.
             */
            pristine: {
                type: Boolean,
                notify: true,
                value: true
            },
            /**
             * Indicates that a selection is necessary (ns-form would have to enforce)
             */
            required: {
                type: Boolean
            },
            /**
             * The error message shown when the input is invalid
             */
            requiredMessage: {
                type: String,
                value: "Please choose an item."
            },
            /**
             * The selected value
             */
            selected: {
                type: Number,
                notify: true
            },
            /**
             * Holds state of which dropdown item was selected
             */
            selectedItem: {
                type: Object,
                value: () => {
                    return {}
                }
            },
            /**
             * The copy value of the selection
             */
            selectedText: {
                type: String,
                notify: true,
                value: () => {
                    return ''
                }
            },
            lockSelectedText: {
                type: Boolean
            },
            /**
             * The value of the selection
             */
            selectedValue: {
                type: String,
                notify: true,
                value: () => {
                    return ''
                }
            },
            /**
             * Passes the tab index down to shadow-dom elements.
             * TODO: use for keyboard navigation
             */
            tabIndex: {
                type: String,
            },
            /**
             * Cuts all selection copy to a common character count
             */
            trimLength: {
                type: Number,
                value: 15
            },
            /**
             * Need to document and use or remove
             */
            isInitUpdate: {
                type: Boolean,
                value: false
            },
            /**
             * Need to document and use or remove
             */
            showAll: {
                type: Boolean,
                value: false
            },
            /**
             * There is are designs with a taller dropdown with button next to it
             */
            tall: {
                type: Boolean,
                value: false
            },

            /**
             * Stores the list of light-dom children for later use (e.g. clearing the inputs)
             */
            _distNodeList: {
                type: Array,
                value: []
            },
            /**
             * Prevents drawer from opening
             */
            stayClosed: Boolean,

            multiLine: {
                type: Boolean,
                value: false
            },

            /**
             * What to display in selectedText when nothing is selected
             */
            placeHolder: {
                type: String,
            },
        }
    }

    ready() {
        super.ready();

        if (this.placeHolder) this.selectedText = this.placeHolder;

        if (!this.tabIndex || this.tabIndex === -1) {
            this.tabIndex = '100';
        }

        this.shadowRoot.querySelector('.dropdown-area').addEventListener('mouseleave', () => {
            this.close();
        });

    }

    connectedCallback() {
        super.connectedCallback()
        // Grab the distributed children from slots and filter for only dom nodes
        this._distNodesObserver = new FlattenedNodesObserver(this, (info) => {
            this._processNewNodes(info.addedNodes.filter((node) => {
                return (node.nodeType === Node.ELEMENT_NODE)
            }));
        });
    }

    /**
     * Disables input
     */
    disable() {
        this.disabled = true;
    }

    /**
     * Enables input
     */
    enable() {
        this.disabled = false;
    }

    /**
     * Store the filtered list of dom nodes in a property for later use
     * @param {dom nodelist} nodeList
     */
    _processNewNodes(nodeList) {
        for (const key in nodeList) {
            if (nodeList.hasOwnProperty(key)) {
                const node = nodeList[key];
                this._distNodeList.push(node);
            }
        }
    }

    /**
     * Opens and closes the content area if not disabled.
     */
    toggle(e) {
        if (!this.disabled) {
            if (!this.stayClosed) {
                this.$.dropdownContent.toggle();
                this.opened = !this.opened;
                this.pristine = false;
                if (this.autovalidate && !this.opened) {
                    this.validate();
                }
            }
        }
    }

    /**
     * Close the dropdown content container
     */
    close(e) {
        if (!this.stayClosed) {
            this.$.dropdownContent.hide();
            this.opened = false;
            if (this.autovalidate) {
                this.validate();
            }
        }
    }

    /**
     * Returns a truncated value of the copy for the dropdown
     */
    trim(str) {
        if (!this.dontTrim) {
            return (str.length > this.trimLength) ? str.substring(0, this.trimLength) + '...' : str;
        } else {
            return str;
        }
    }

    /**
     * Listener that sets the selected value as the display value and dispatches the selection
     */
    processSelect(e) {
        this.$.selectedItem.innerHTML = this.lockSelectedText ? this.selectedText : e.detail.item.innerHTML;
        this.selectedItem.text = this.lockSelectedText ? this.selectedText : e.detail.item.innerHTML;
        this.selectedValue = this.keepSelectedAsHtml ? e.detail.item.value : e.detail.item.innerText.trim();
        this.selectedItem.value = e.detail.item.dataset.value || e.detail.item.value || e.detail.item.getAttribute("value");
        let paperItems = this.$.dropdownListbox.getContentChildNodes().filter(obj => {
            return obj.nodeName === "PAPER-ITEM";
        });
        for (var i = 0; i < paperItems.length; i++) {
            if (paperItems[i].className.includes("iron-selected")) this.index = i;
        }
        this.dispatchEvent(new CustomEvent('nsDropdownSelect', {
            composed: this.composed,
            bubbles: this.bubbles,
            detail: {
                selectedItem: this.selectedItem,
                selected: this.selected,
                index: this.index
            }
        }));
        if (this.opened) this.close(e);
    }

    /**
     * Reset the dropdown to an unselected/initial state
     */
    clear() {
        // Clear the values and text
        this.selectedItem.text = '';
        this.selectedItem.value = '';
        this.selectedText = '';
        this.selectedValue = '';
        // Unset the selected item
        this.$.dropdownListbox.selected = -1;
        // Look for the node with a class of 'iron-selected' that belongs to this instance
        for (let i = 0; i < this._distNodeList.length; i++) {
            const node = this._distNodeList[i];
            if (node.classList.contains('iron-selected') && this.id === node.parentElement.id) {
                node.classList.remove('iron-selected');
            }
        }
    }

    /**
     * Simple validation to see if anything was selected or not
     */
    validate() {
        if (!this.selectedItem.text) {
            this.invalid = true;
        } else {
            this.invalid = false;
        }
        this.showErrors();
        return !this.invalid;
    }

    /**
     * Show errors if invalid. This is used by ns-form for showing or clearing out errors.
     */
    showErrors() {
        const el = this.$.currentSelection;
        const errorEl = this.shadowRoot.querySelector("error-tag");
        if (this.invalid) {
            el.classList.add("error");
            errorEl.style.display = "block";
            this.errorMessage = this.invalidMessage;
        } else {
            el.classList.remove("error");
            errorEl.style.display = "none";
            this.errorMessage = "";
        }
    }

    /**
     * Sets the index of the current selection
     */
    _processInitSelect() {
        setTimeout(() => {
            this.$.dropdownListbox.selectIndex(this.initSelect)
        }, 100);
    }

    _processSelectedChanged() {
        setTimeout(() => {
            this.$.dropdownListbox.selectIndex(0)
        }, 100);
    }

}

customElements.define(NsSlottedDropdown.is, NsSlottedDropdown);
